/**
 * Auto-Save Hook
 * 
 * Provides automatic saving of form data to localStorage with debouncing.
 * Includes utilities for loading and clearing saved data.
 */

import { useEffect, useRef, useCallback } from 'react';
import { AUTOSAVE_DELAY_MS, AUTOSAVE_MAX_AGE_MS } from '../utils/constants';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Save data to localStorage
 * @param {string} key - Storage key
 * @param {Object} data - Data to save
 * @returns {boolean} Success status
 */
export function saveData(key, data) {
  if (!key) return false;
  
  try {
    const payload = {
      ...data,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(key, JSON.stringify(payload));
    return true;
  } catch (error) {
    console.error('Auto-save error:', error.message);
    return false;
  }
}

/**
 * Load saved data from localStorage
 * @param {string} key - Storage key
 * @param {Object} options - Options
 * @param {number} options.maxAge - Maximum age in milliseconds (default: 7 days)
 * @returns {Object|null} Saved data or null
 */
export function loadSavedData(key, options = {}) {
  const { maxAge = AUTOSAVE_MAX_AGE_MS } = options;
  
  if (!key) return null;
  
  try {
    const savedData = localStorage.getItem(key);
    if (!savedData) return null;
    
    const parsed = JSON.parse(savedData);
    
    // Check if data has expired
    if (parsed.timestamp && maxAge > 0) {
      const savedTime = new Date(parsed.timestamp).getTime();
      const now = Date.now();
      
      if (now - savedTime > maxAge) {
        // Data is too old, clear it
        clearSavedData(key);
        return null;
      }
    }
    
    return parsed;
  } catch (error) {
    console.error('Load saved data error:', error.message);
    return null;
  }
}

/**
 * Clear saved data from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export function clearSavedData(key) {
  if (!key) return false;
  
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Clear saved data error:', error.message);
    return false;
  }
}

/**
 * Check if saved data exists and is valid
 * @param {string} key - Storage key
 * @returns {boolean} Whether valid data exists
 */
export function hasSavedData(key) {
  const data = loadSavedData(key);
  return data !== null && data.formData !== undefined;
}

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Custom hook for auto-saving data to localStorage with debounce
 * 
 * @param {Object} data - Data to save
 * @param {string} key - localStorage key
 * @param {number} delay - Debounce delay in milliseconds
 * @param {Function} onSavingChange - Callback when saving state changes
 * 
 * @example
 * useAutoSave({ formData, logoPreview }, 'invoice_draft', 2000, setSaving);
 */
export function useAutoSave(
  data,
  key,
  delay = AUTOSAVE_DELAY_MS,
  onSavingChange = null
) {
  const timeoutRef = useRef(null);
  const lastSavedRef = useRef(null);
  const dataRef = useRef(data);
  const onSavingChangeRef = useRef(onSavingChange);
  
  // Keep refs updated with latest values
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  
  useEffect(() => {
    onSavingChangeRef.current = onSavingChange;
  }, [onSavingChange]);
  
  /**
   * Trigger save with debounce
   */
  const scheduleSave = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Notify saving is starting
    onSavingChangeRef.current?.(true);
    
    // Schedule save
    timeoutRef.current = setTimeout(() => {
      const success = saveData(key, dataRef.current);
      
      if (success) {
        lastSavedRef.current = new Date().toISOString();
      }
      
      onSavingChangeRef.current?.(false);
      timeoutRef.current = null;
    }, delay);
  }, [key, delay]);
  
  // Track previous formData to detect real changes
  const prevFormDataRef = useRef(null);
  
  // Effect to trigger save when data changes
  useEffect(() => {
    // Skip if data is empty or hasn't really changed
    if (!data || !key) return;
    
    // Only save if formData actually changed (shallow compare key fields)
    const formData = data.formData;
    const prevFormData = prevFormDataRef.current;
    
    // Skip initial render and identical data
    if (prevFormData !== null) {
      const hasChanged = JSON.stringify(formData) !== JSON.stringify(prevFormData);
      if (!hasChanged) return;
    }
    
    prevFormDataRef.current = formData;
    scheduleSave();
    
    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, key, scheduleSave]);
  
  /**
   * Force an immediate save
   */
  const saveNow = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    return saveData(key, data);
  }, [key, data]);
  
  /**
   * Get the last saved timestamp
   */
  const getLastSaved = useCallback(() => {
    return lastSavedRef.current;
  }, []);
  
  return {
    saveNow,
    getLastSaved,
  };
}

export default useAutoSave;